import type {
  ApiEndpoint,
  ApiSurface,
  CredentialMetadata,
  CredentialUpsert,
  EnvironmentId,
  HealthReport,
  HealthRunRequest,
  Run,
  RunDetail,
  RunRequest,
  TranscriptSegment,
  TriggerObservation,
} from '@sfpbxtestbot/contracts';
import type { EnvironmentDefinition } from '@sfpbxtestbot/env-matrix';

/** A problem the server could explain in words, ready to render next to a field. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problems: { field: string; message: string }[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const attempts = method === 'GET' || method === 'HEAD' ? 8 : 1;
  let response: Response | undefined;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      response = await fetch(`/api${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...init?.headers,
        },
      });
      if (response.status !== 503 || attempt === attempts - 1) break;
    } catch (error) {
      lastError = error;
      if (attempt === attempts - 1) throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 400));
  }

  if (!response) {
    throw lastError instanceof Error ? lastError : new Error('The API did not respond.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    if (response.status === 401 && path !== '/auth/me' && path !== '/auth/config') {
      window.dispatchEvent(new Event('sfpbx:unauthorized'));
    }
    const body = payload as { error?: string; problems?: { field: string; message: string }[] };
    throw new ApiError(
      body?.error ?? `The request failed with status ${response.status}.`,
      response.status,
      body?.problems ?? [],
    );
  }

  return payload as T;
}

export interface CatalogResponse {
  endpoints: ApiEndpoint[];
  summary: {
    total: number;
    bySurface: Record<ApiSurface, number>;
    byMode: Record<'live' | 'reachability' | 'manual', number>;
  };
  groups: Record<ApiSurface, string[]>;
}

export interface LogEntry {
  id: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  stepKey?: string;
  at: string;
}

export interface AuthUser {
  email: string;
  name: string;
  picture: string;
}

export const api = {
  status: () =>
    request<{ ok: boolean; version: string; activeRuns: number; maxConcurrentRuns: number }>(
      '/status',
    ),

  authConfig: () =>
    request<{ googleConfigured: boolean; allowedDomain: string }>('/auth/config'),

  me: () => request<AuthUser>('/auth/me'),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  catalog: () => request<CatalogResponse>('/catalog'),

  environments: () => request<{ environments: EnvironmentDefinition[] }>('/environments'),

  credentials: () =>
    request<{ credentials: CredentialMetadata[]; labels: Record<string, string> }>('/credentials'),

  saveCredential: (body: CredentialUpsert) =>
    request<void>('/credentials', { method: 'PUT', body: JSON.stringify(body) }),

  deleteCredential: (kind: string, environment?: EnvironmentId | null) =>
    request<void>(
      `/credentials/${kind}${environment ? `?environment=${environment}` : ''}`,
      { method: 'DELETE' },
    ),

  salesforceConnectStatus: () =>
    request<{
      status: 'idle' | 'waiting' | 'saved' | 'failed';
      target?: 'salesforce' | 'google-voice';
      environment: EnvironmentId | null;
      orgUrl?: string;
      message?: string;
    }>('/credentials/salesforce/connect'),

  startGoogleVoiceConnect: () =>
    request<{ status: 'idle' | 'waiting' | 'saved' | 'failed'; message?: string }>(
      '/credentials/google-voice/connect',
      { method: 'POST' },
    ),

  startSalesforceConnect: (body: { salesforceOrgUrl: string; environment: EnvironmentId | null }) =>
    request<{
      status: 'idle' | 'waiting' | 'saved' | 'failed';
      environment: EnvironmentId | null;
      orgUrl?: string;
      message?: string;
    }>('/credentials/salesforce/connect', { method: 'POST', body: JSON.stringify(body) }),

  cancelSalesforceConnect: () =>
    request<{ status: string }>('/credentials/salesforce/connect/cancel', { method: 'POST' }),

  salesforceConnectScreen: () =>
    fetch('/api/credentials/salesforce/connect/screen', { credentials: 'same-origin' }),

  salesforceConnectInput: (body: {
    action: 'click' | 'type' | 'press';
    x?: number;
    y?: number;
    text?: string;
    key?: string;
  }) =>
    request<void>('/credentials/salesforce/connect/input', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  healthReports: (limit = 20) =>
    request<{ reports: HealthReport[] }>(`/health-reports?limit=${limit}`),

  healthReport: (id: string) => request<HealthReport>(`/health-reports/${id}`),

  runHealthCheck: (body: HealthRunRequest) =>
    request<HealthReport>('/health-reports', { method: 'POST', body: JSON.stringify(body) }),

  runs: (params: { limit?: number; offset?: number; environment?: EnvironmentId; search?: string } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value));
    }
    const suffix = query.toString();
    return request<{ runs: Run[]; total: number }>(`/runs${suffix ? `?${suffix}` : ''}`);
  },

  run: (id: string) => request<RunDetail>(`/runs/${id}`),

  runLogs: (id: string, after = 0) =>
    request<{ logs: LogEntry[] }>(`/runs/${id}/logs?after=${after}`),

  runCall: (id: string) =>
    request<{ transcript: TranscriptSegment[]; triggers: TriggerObservation[] }>(
      `/runs/${id}/call`,
    ),

  startRun: (body: RunRequest) =>
    request<RunDetail>('/runs', { method: 'POST', body: JSON.stringify(body) }),

  stopRun: (id: string) => request<{ stopping: boolean }>(`/runs/${id}/stop`, { method: 'POST' }),

  artifactUrl: (runId: string, artifactId: string) => `/api/runs/${runId}/artifacts/${artifactId}`,

  /** Self-contained report with every screenshot embedded. */
  reportUrl: (runId: string, download = false) =>
    `/api/runs/${runId}/report.html${download ? '?download=true' : ''}`,

  catalogMarkdownUrl: '/api/catalog/export.md',
  catalogCsvUrl: '/api/catalog/export.csv',
};
